// PlotSurveyForm.js
import React, { useState } from 'react';

const PlotSurveyForm = () => {
  // State for the survey request form data
  const [plotData, setPlotData] = useState({
    plotId: '',
    address: '',
    area: '',
    ownerName: '',
    currentUsage: '',
    notes: '',
    contactEmail: '',
    contactPhone: ''
  });

  // State for form submission message and loading status
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Handles input changes for the form fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setPlotData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  // Handles form submission logic
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevents default browser form submission
    setIsLoading(true); // Activates loading indicator

    try {
      // Simulate an asynchronous API call to submit the form data
      // In a real application, replace this with an actual fetch or Axios call to your backend
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate network delay for 1.5 seconds

      console.log('Plot Survey Request Submitted:', plotData); // Log data to console for demonstration
      setMessage('Your plot survey request has been received! Our team will review the details and contact you shortly.');

      // Clear the form fields after successful submission
      setPlotData({
        plotId: '',
        address: '',
        area: '',
        ownerName: '',
        currentUsage: '',
        notes: '',
        contactEmail: '',
        contactPhone: ''
      });
    } catch (error) {
      console.error('Error submitting plot data:', error); // Log any errors
      setMessage('There was an error submitting your request. Please try again later.');
    } finally {
      setIsLoading(false); // Deactivates loading indicator
      // Clear the submission message after a few seconds
      setTimeout(() => setMessage(''), 7000);
    }
  };

  const formContainerStyle = {
    backgroundColor: '#fff',
    padding: '32px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '800px', // Adjusted max-width for better form layout
    margin: '32px auto',
    fontFamily: 'Inter, sans-serif',
    color: '#333',
  };

  const formTitleStyle = {
    fontSize: '2.25rem', // text-3xl
    fontWeight: 'bold',
    color: '#1a202c', // gray-800
    marginBottom: '24px',
    textAlign: 'center',
  };

  const formDescriptionStyle = {
    color: '#4a5568', // gray-600
    marginBottom: '32px',
    textAlign: 'center',
  };

  const formGroupStyle = {
    marginBottom: '24px', // space-y-6 equivalent
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.875rem', // text-sm
    fontWeight: '500', // font-medium
    color: '#4a5568', // gray-700
    marginBottom: '4px', // mb-1
  };

  const inputStyle = {
    display: 'block',
    width: '100%',
    padding: '10px 16px', // px-4 py-2
    border: '1px solid #cbd5e0', // border border-gray-300
    borderRadius: '8px', // rounded-lg
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)', // shadow-sm
    fontSize: '0.875rem', // sm:text-sm
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
  };

  const focusInputStyle = {
    borderColor: '#3b82f6', // focus:border-blue-500
    boxShadow: '0 0 0 3px rgba(96, 165, 250, 0.5)', // focus:ring-blue-500
    outline: 'none',
  };

  const selectStyle = {
    ...inputStyle, // Inherit base input styles
    appearance: 'none', // Remove default select arrow
    paddingRight: '30px', // Make space for custom arrow if needed
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none'%3e%3cpath d='M7 7l3-3 3 3m0 6l-3 3-3-3' stroke='%234A5568' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: '1em',
  };

  const buttonStyle = {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '12px 16px', // py-2 px-4
    border: '1px solid transparent',
    borderRadius: '8px', // rounded-lg
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    fontSize: '1.125rem', // text-lg
    fontWeight: '500', // font-medium
    color: '#fff',
    backgroundColor: '#2563eb', // bg-blue-600
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
  };

  const buttonHoverStyle = {
    backgroundColor: '#1d4ed8', // hover:bg-blue-700
  };

  const buttonDisabledStyle = {
    opacity: '0.7',
    cursor: 'not-allowed',
  };

  const messageStyle = {
    marginTop: '16px',
    padding: '12px',
    borderRadius: '8px',
    textAlign: 'center',
    fontSize: '0.875rem', // text-sm
  };

  const successMessageStyle = {
    backgroundColor: '#d1fae5', // bg-green-100
    color: '#065f46', // text-green-800
  };

  const errorMessageStyle = {
    backgroundColor: '#fee2e2', // bg-red-100
    color: '#991b1b', // text-red-800
  };

  const spinnerStyle = {
    animation: 'spin 1s linear infinite',
    marginLeft: '-4px', // -ml-1
    marginRight: '12px', // mr-3
    height: '20px', // h-5
    width: '20px', // w-5
    color: '#fff',
  };
  
  const spinnerKeyframes = `@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }`;


  return (
    <div style={formContainerStyle}>
      <h2 style={formTitleStyle}>Request Your Plot Survey</h2>
      <p style={formDescriptionStyle}>
        Please provide the necessary details below, and our team will get back to you to schedule your comprehensive plot survey.
      </p>

      <form onSubmit={handleSubmit} style={formGroupStyle}>
        {/* Contact Information */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginBottom: '24px' }}> {/* md:grid-cols-2 */}
          <div style={formGroupStyle}>
            <label htmlFor="contactEmail" style={labelStyle}>
              Your Email <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="email"
              id="contactEmail"
              name="contactEmail"
              value={plotData.contactEmail}
              onChange={handleChange}
              required
              style={inputStyle}
              onFocus={(e) => Object.assign(e.target.style, focusInputStyle)}
              onBlur={(e) => Object.assign(e.target.style, inputStyle)}
            />
          </div>
          <div style={formGroupStyle}>
            <label htmlFor="contactPhone" style={labelStyle}>
              Your Phone Number <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="tel"
              id="contactPhone"
              name="contactPhone"
              value={plotData.contactPhone}
              onChange={handleChange}
              required
              style={inputStyle}
              onFocus={(e) => Object.assign(e.target.style, focusInputStyle)}
              onBlur={(e) => Object.assign(e.target.style, inputStyle)}
            />
          </div>
        </div>

        {/* Plot ID */}
        <div style={formGroupStyle}>
          <label htmlFor="plotId" style={labelStyle}>
            Plot ID (if known)
          </label>
          <input
            type="text"
            id="plotId"
            name="plotId"
            value={plotData.plotId}
            onChange={handleChange}
            style={inputStyle}
            onFocus={(e) => Object.assign(e.target.style, focusInputStyle)}
            onBlur={(e) => Object.assign(e.target.style, inputStyle)}
          />
        </div>

        {/* Address/Location */}
        <div style={formGroupStyle}>
          <label htmlFor="address" style={labelStyle}>
            Address / Location of Plot <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <textarea
            id="address"
            name="address"
            value={plotData.address}
            onChange={handleChange}
            rows="3"
            required
            style={inputStyle}
            onFocus={(e) => Object.assign(e.target.style, focusInputStyle)}
            onBlur={(e) => Object.assign(e.target.style, inputStyle)}
          ></textarea>
        </div>

        {/* Area */}
        <div style={formGroupStyle}>
          <label htmlFor="area" style={labelStyle}>
            Estimated Area (e.g., 1200 sq. ft, 0.5 acres) <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="text"
            id="area"
            name="area"
            value={plotData.area}
            onChange={handleChange}
            required
            style={inputStyle}
            onFocus={(e) => Object.assign(e.target.style, focusInputStyle)}
            onBlur={(e) => Object.assign(e.target.style, inputStyle)}
          />
        </div>

        {/* Owner Name */}
        <div style={formGroupStyle}>
          <label htmlFor="ownerName" style={labelStyle}>
            Owner Name (for record keeping)
          </label>
          <input
            type="text"
            id="ownerName"
            name="ownerName"
            value={plotData.ownerName}
            onChange={handleChange}
            style={inputStyle}
            onFocus={(e) => Object.assign(e.target.style, focusInputStyle)}
            onBlur={(e) => Object.assign(e.target.style, inputStyle)}
          />
        </div>

        {/* Current Usage */}
        <div style={formGroupStyle}>
          <label htmlFor="currentUsage" style={labelStyle}>
            Current Usage
          </label>
          <select
            id="currentUsage"
            name="currentUsage"
            value={plotData.currentUsage}
            onChange={handleChange}
            style={selectStyle}
            onFocus={(e) => Object.assign(e.target.style, focusInputStyle)}
            onBlur={(e) => Object.assign(e.target.style, inputStyle)}
          >
            <option value="">Select Usage</option>
            <option value="Residential">Residential</option>
            <option value="Commercial">Commercial</option>
            <option value="Agricultural">Agricultural</option>
            <option value="Industrial">Industrial</option>
            <option value="Vacant">Vacant</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Notes/Observations */}
        <div style={formGroupStyle}>
          <label htmlFor="notes" style={labelStyle}>
            Any specific requirements or observations?
          </label>
          <textarea
            id="notes"
            name="notes"
            value={plotData.notes}
            onChange={handleChange}
            rows="4"
            style={inputStyle}
            onFocus={(e) => Object.assign(e.target.style, focusInputStyle)}
            onBlur={(e) => Object.assign(e.target.style, inputStyle)}
          ></textarea>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          style={{ ...buttonStyle, ...(isLoading ? buttonDisabledStyle : {}), ':hover': isLoading ? {} : buttonHoverStyle }}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <style>{spinnerKeyframes}</style>
              <svg style={spinnerStyle} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle style={{ opacity: '0.25' }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path style={{ opacity: '0.75' }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              'Submitting...'
            </>
          ) : (
            'Submit Survey Request'
          )}
        </button>

        {/* Submission Message */}
        {message && (
          <div style={{ ...messageStyle, ...(message.includes('error') ? errorMessageStyle : successMessageStyle) }}>
            {message}
          </div>
        )}
      </form>
    </div>
  );
};

export default PlotSurveyForm;